import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  BookUser,
  CalendarDays,
  CheckCircle,
  ClipboardList,
  Hourglass,
  IndianRupee,
  Phone,
  RefreshCw,
  ShoppingCart,
  Sparkles,
  TrendingUp,
  UserCheck,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AdminLayout from "../components/admin/AdminLayout";
import AdminTeamPage from "../components/admin/AdminTeamPage";
import CreateCustomerPage from "../components/admin/CreateCustomerPage";
import CallRequestsPage from "../components/admin/CallRequestsPage";
import CAProfilesPage from "../components/admin/CAProfilesPage";
import ChangePasswordModal from "../components/admin/ChangePasswordModal";
import ConfirmModal from "../components/admin/ConfirmModal";
import ContactsPanel from "../components/admin/ContactsPanel";
import CustomerLogDetail from "../components/admin/CustomerLogDetail";
import CustomerLogsList from "../components/admin/CustomerLogsList";
import LogsPanel from "../components/admin/LogsPanel";
import MembersDirectory from "../components/admin/MembersDirectory";
import OrderDetailTabs from "../components/admin/OrderDetailTabs";
import OrdersTable from "../components/admin/OrdersTable";
import InvoicesPanel from "../components/admin/InvoicesPanel";
import PaymentsPanel from "../components/admin/PaymentsPanel";
import ProfilePage from "../components/admin/ProfilePage";
import StalledUsersPanel from "../components/admin/StalledUsersPanel";
import UsersTable from "../components/admin/UsersTable";
import VisitorFunnelPanel from "../components/admin/VisitorFunnelPanel";
import AiReportPanel from "../components/admin/AiReportPanel";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { resolveUserImageUrl } from "../lib/avatar";

function StatusBadge({ status }) {
  const cls =
    {
      active: "bg-blue-100 text-blue-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
      admin_pending: "bg-orange-100 text-orange-700",
    }[status] || "bg-gray-100 text-gray-600";
  return (
    <span
      className={`text-xs font-body font-medium px-2.5 py-0.5 rounded-full ${cls}`}
    >
      {status?.replace("_", " ")}
    </span>
  );
}

// Role-aware quick-link tiles. CA-level tiles surface for every role;
// admin/super-admin tiles are filtered out for plain CA accounts.
const QUICK_LINKS = [
  { label: "Orders",         to: "/admin/orders",         icon: ClipboardList, roles: null,                    tint: "#6C63FF" },
  { label: "Call Requests",  to: "/admin/call-requests",  icon: Phone,         roles: null,                    tint: "#25D366" },
  { label: "Customers",      to: "/admin/users",          icon: Users,         roles: ["admin", "super_admin"], tint: "#0EA5E9" },
  { label: "Stalled Users",  to: "/admin/stalled-users",  icon: Hourglass,     roles: ["super_admin"],         tint: "#F59E0B" },
  { label: "CA Profiles",    to: "/admin/ca-profiles",    icon: UserCheck,     roles: ["admin", "super_admin"], tint: "#A78BFA" },
  { label: "Members",        to: "/admin/members",        icon: BookUser,      roles: ["admin", "super_admin"], tint: "#06B6D4" },
  { label: "Customer Logs",  to: "/admin/customer-logs",  icon: Activity,      roles: ["super_admin"],         tint: "#EC4899" },
  { label: "AI Reports",     to: "/admin/ai-reports",     icon: Sparkles,      roles: ["super_admin"],         tint: "#6C63FF" },
  { label: "Team",           to: "/admin/team",           icon: UserCheck,     roles: ["super_admin"],         tint: "#10B981" },
];

const greetingFor = (date = new Date()) => {
  const h = date.getHours();
  if (h < 5)  return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

function KpiCard({ icon: Icon, label, value, accent, footer, to }) {
  const Wrapper = to ? Link : "div";
  return (
    <Wrapper
      {...(to ? { to } : {})}
      className="group relative overflow-hidden rounded-2xl bg-white border border-[#E8E6F8] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-[0.08] blur-2xl"
        style={{ backgroundColor: accent }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}18` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        {to && (
          <ArrowUpRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6C63FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        )}
      </div>
      <p className="relative text-[11px] font-body font-medium text-[#6B7280] mt-4 uppercase tracking-wider">
        {label}
      </p>
      <p className="relative text-[28px] font-heading font-bold text-[#1A1A2E] leading-tight mt-1">
        {value}
      </p>
      {footer && (
        <p className="relative text-[11px] font-body text-[#9CA3AF] mt-1">{footer}</p>
      )}
    </Wrapper>
  );
}

function ActionCallout({ icon: Icon, accent, count, label, hint, to }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 bg-white border border-[#E8E6F8] rounded-2xl px-4 py-3 hover:border-[#6C63FF]/40 hover:shadow-sm transition-all"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${accent}18` }}
      >
        <Icon className="h-4.5 w-4.5" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-heading font-semibold text-[#1A1A2E] truncate">
          <span className="text-base">{count}</span> {label}
        </p>
        <p className="text-[11px] text-[#9CA3AF] font-body truncate">{hint}</p>
      </div>
      <ArrowUpRight className="h-4 w-4 text-[#9CA3AF] group-hover:text-[#6C63FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </Link>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/stats");
      setStats(data);
    } catch (err) {
      if (err.response?.status === 403) navigate("/sign-in");
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;
  if (!stats) return null;

  const role = user?.role;
  const firstName = user?.name?.split(" ")?.[0] || "there";
  const roleLabel =
    role === "super_admin" ? "Super Admin" :
    role === "admin"        ? "Admin"        :
    role === "ca"           ? "CA"           : (role || "Staff");
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const completionRate = stats.total_orders
    ? Math.round((stats.completed_orders / stats.total_orders) * 100)
    : 0;
  const activeRate = stats.total_users
    ? Math.round((stats.active_users / stats.total_users) * 100)
    : 0;

  // Focus strip — surface only what genuinely needs attention right now.
  const focusItems = [];
  if (stats.pending_admin_steps > 0) {
    focusItems.push({
      icon: AlertTriangle, accent: "#F97316",
      count: stats.pending_admin_steps,
      label: stats.pending_admin_steps === 1 ? "step needs CA action" : "steps need CA action",
      hint: "Customers waiting on you",
      to: "/admin/orders?status=admin_pending",
    });
  }
  if (stats.pending_call_requests > 0) {
    focusItems.push({
      icon: Phone, accent: "#25D366",
      count: stats.pending_call_requests,
      label: stats.pending_call_requests === 1 ? "call request open" : "call requests open",
      hint: "Awaiting an outreach",
      to: "/admin/call-requests",
    });
  }
  if (stats.active_orders > 0) {
    focusItems.push({
      icon: ClipboardList, accent: "#6C63FF",
      count: stats.active_orders,
      label: stats.active_orders === 1 ? "active order in flight" : "active orders in flight",
      hint: "In-progress workflows",
      to: "/admin/orders",
    });
  }

  const visibleQuickLinks = QUICK_LINKS.filter(
    (l) => l.roles === null || l.roles.includes(role)
  );

  return (
    <div className="space-y-6">
      {/* ── Hero greeting ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] text-white px-6 py-7 sm:px-8 sm:py-9"
      >
        <div className="absolute -top-24 -right-12 w-72 h-72 bg-[#A78BFA]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-12 w-72 h-72 bg-[#22D3EE]/15 rounded-full blur-3xl" />

        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1 mb-4">
              <Sparkles className="h-3 w-3 text-[#FBBF24]" />
              <span className="text-[10px] font-body font-semibold tracking-[0.18em] uppercase text-white/85">
                {roleLabel} Dashboard
              </span>
            </div>
            <h1 className="text-2xl sm:text-[28px] font-heading font-bold leading-tight">
              {greetingFor()}, {firstName}.
            </h1>
            <p className="text-sm text-white/70 font-body mt-1.5 flex items-center gap-2 flex-wrap">
              <CalendarDays className="h-3.5 w-3.5" />
              <span>{today}</span>
              <span className="text-white/30">·</span>
              <span>
                {focusItems.length > 0
                  ? `${focusItems.length} item${focusItems.length > 1 ? "s" : ""} need your attention`
                  : "All caught up — nothing pending"}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">
                Revenue
              </p>
              <p className="text-2xl font-heading font-bold mt-0.5 flex items-center gap-1">
                {inr(stats.total_revenue)}
                <TrendingUp className="h-4 w-4 text-[#34D399]" />
              </p>
            </div>
            <div className="h-10 w-px bg-white/15" />
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.18em] text-white/60">
                Completion
              </p>
              <p className="text-2xl font-heading font-bold mt-0.5">
                {completionRate}%
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Focus strip ────────────────────────────────────────────────── */}
      {focusItems.length > 0 && (
        <div>
          <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] mb-2 px-1">
            Today's Focus
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {focusItems.map((item, i) => <ActionCallout key={i} {...item} />)}
          </div>
        </div>
      )}

      {/* ── Primary KPIs ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={IndianRupee}
          label="Total Revenue"
          value={inr(stats.total_revenue)}
          accent="#10B981"
          footer="All captured payments"
          to="/admin/payments"
        />
        <KpiCard
          icon={ShoppingCart}
          label="Total Orders"
          value={stats.total_orders}
          accent="#6C63FF"
          footer={`${stats.active_orders} active right now`}
          to="/admin/orders"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Pending CA Actions"
          value={stats.pending_admin_steps}
          accent="#F97316"
          footer="Customers waiting"
          to="/admin/orders?status=admin_pending"
        />
        <KpiCard
          icon={CheckCircle}
          label="Completed Orders"
          value={stats.completed_orders}
          accent="#4CAF50"
          footer={`${completionRate}% completion rate`}
          to="/admin/orders?status=completed"
        />
      </div>

      {/* ── Charts row ────────────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E8E6F8] flex items-center justify-between">
            <div>
              <h2 className="text-sm font-heading font-bold text-[#1A1A2E]">
                Recent Activity
              </h2>
              <p className="text-[11px] text-[#9CA3AF] font-body">
                Latest captured payments
              </p>
            </div>
            <Link
              to="/admin/payments"
              className="inline-flex items-center gap-1 text-xs text-[#6C63FF] font-body font-medium hover:underline"
            >
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {(stats.recent_payments || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <IndianRupee className="h-7 w-7 text-[#9CA3AF] mb-2" />
              <p className="text-sm text-[#9CA3AF] font-body">No payments yet</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E6F8]">
              {(stats.recent_payments || []).slice(0, 6).map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-[#F5F4FB] transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#6C63FF]/10 text-[#6C63FF] flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
                      {(p.user_name || "?")[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-body font-medium text-[#1A1A2E] truncate">
                        {p.user_name || "Unknown"}
                      </p>
                      <p className="text-[11px] text-[#9CA3AF] font-body">
                        Order #{p.order_id}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-heading font-bold text-[#10B981]">
                      {inr(p.amount)}
                    </p>
                    <p className="text-[10px] text-[#9CA3AF] font-body">
                      {p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-IN") : "–"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
          <div className="mb-4">
            <h2 className="text-sm font-heading font-bold text-[#1A1A2E]">
              Orders by Service
            </h2>
            <p className="text-[11px] text-[#9CA3AF] font-body">
              Top {Math.min((stats.orders_by_service || []).length, 8)} services
            </p>
          </div>
          {(stats.orders_by_service || []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-7 w-7 text-[#9CA3AF] mb-2" />
              <p className="text-sm text-[#9CA3AF] font-body">No data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={stats.orders_by_service.slice(0, 8)}
                layout="vertical"
                margin={{ left: 4, right: 16, top: 4, bottom: 4 }}
              >
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6C63FF" />
                    <stop offset="100%" stopColor="#A78BFA" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6F8" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                <YAxis
                  type="category"
                  dataKey="service_id"
                  width={100}
                  tick={{ fontSize: 10, fill: "#6B7280" }}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12, borderRadius: 8, border: "1px solid #E8E6F8",
                  }}
                />
                <Bar dataKey="count" fill="url(#barGrad)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Customer & activity metrics ───────────────────────────────── */}
      <div>
        <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] mb-2 px-1">
          Customers & Activity
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Users}
            label="Total Users"
            value={stats.total_users}
            accent="#6C63FF"
            footer={`${stats.active_users} active`}
            to="/admin/users"
          />
          <KpiCard
            icon={Activity}
            label="Active Users"
            value={stats.active_users}
            accent="#0EA5E9"
            footer={`${activeRate}% of base`}
            to="/admin/users"
          />
          <KpiCard
            icon={Phone}
            label="Call Requests"
            value={stats.total_call_requests}
            accent="#25D366"
            footer={`${stats.pending_call_requests} pending`}
            to="/admin/call-requests"
          />
          <KpiCard
            icon={XCircle}
            label="Cancelled Orders"
            value={stats.cancelled_orders}
            accent="#E57373"
            footer="Recoverable winbacks"
            to={role === "super_admin" ? "/admin/stalled-users" : "/admin/orders?status=cancelled"}
          />
        </div>
      </div>

      {/* ── Quick links ──────────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-[#9CA3AF] mb-2 px-1">
          Jump to
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {visibleQuickLinks.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="group flex items-center gap-3 bg-white border border-[#E8E6F8] rounded-2xl px-4 py-3 hover:border-[#6C63FF]/40 hover:shadow-sm hover:-translate-y-0.5 transition-all"
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${q.tint}18` }}
              >
                <q.icon className="h-4 w-4" style={{ color: q.tint }} />
              </div>
              <span className="text-sm font-body font-medium text-[#1A1A2E] truncate flex-1">
                {q.label}
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 text-[#9CA3AF] group-hover:text-[#6C63FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminOrdersPage() {
  return <OrdersTable />;
}

export function AdminOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusModal, setStatusModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await API.get(`/admin/orders/${orderId}`);
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load order");
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (status) => {
    try {
      await API.put(`/admin/orders/${orderId}/status`, { status });
      setStatusModal(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Update failed");
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage msg={error} />;
  if (!order) return null;

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate("/admin/orders")}
        className="flex items-center gap-1.5 text-sm text-[#6C63FF] hover:underline font-body"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Orders
      </button>

      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-heading font-bold text-[#1A1A2E]">
                {order.service_name}
              </h2>
              <StatusBadge status={order.status} />
            </div>
            <p className="text-sm text-[#6B7280] font-body">
              Order #{order.id} ·{" "}
              {order.type === "recurring" ? "Recurring" : "One-time"} · Client:{" "}
              <strong>{order.user?.name}</strong> ({order.user?.email})
            </p>
            <p className="text-xs text-[#9CA3AF] font-body mt-1">
              Assigned CA:{" "}
              {order.ca?.name ? (
                <strong className="text-[#6C63FF]">{order.ca.name}</strong>
              ) : (
                <em className="text-[#9CA3AF]">Unassigned</em>
              )}
              {order.ca?.email && (
                <span className="text-[#9CA3AF]"> ({order.ca.email})</span>
              )}
            </p>
            <p className="text-xs text-[#9CA3AF] font-body mt-0.5">
              Created {new Date(order.created_at).toLocaleString("en-IN")}
              {order.estimated_completion &&
                ` · Estimated ${new Date(order.estimated_completion).toLocaleDateString("en-IN")}`}
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => load()}
              className="flex items-center gap-1.5 text-xs font-body px-3 py-2 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] text-[#6B7280]"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            {order.status === "active" && (
              <>
                <button
                  onClick={() => setStatusModal("completed")}
                  className="flex items-center gap-1.5 text-xs font-body px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg"
                >
                  <CheckCircle className="h-3.5 w-3.5" /> Mark Completed
                </button>
                <button
                  onClick={() => setStatusModal("cancelled")}
                  className="flex items-center gap-1.5 text-xs font-body px-3 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                >
                  <X className="h-3.5 w-3.5" /> Cancel Order
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#E8E6F8] flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="flex-1">
            {(() => {
              // Derive progress from steps so admin-driven completions (CA
              // marking a step from this panel) are reflected immediately,
              // even if the orders.progress_percentage column hasn't been
              // refreshed by the worker.
              const totalSteps = (order.steps || []).length;
              const doneSteps = (order.steps || []).filter(
                (s) => s.status === "completed",
              ).length;
              const derivedPct = totalSteps
                ? Math.round((doneSteps / totalSteps) * 100)
                : 0;
              const pct = Math.max(derivedPct, order.progress_percentage ?? 0);
              return (
                <>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-body text-[#6B7280]">
                      Overall Progress
                    </span>
                    <span className="text-xs font-heading font-bold text-[#6C63FF]">
                      {pct}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-[#E8E6F8] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#6C63FF] rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] font-body mt-1">
                    {doneSteps} of {totalSteps} steps completed
                  </p>
                </>
              );
            })()}
          </div>
          {(order.payments || []).length > 0 && (
            <div className="flex-shrink-0 bg-[#F5F4FB] rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wide">
                Total Paid
              </p>
              <p className="text-base font-heading font-black text-[#6C63FF]">
                ₹
                {(order.payments || [])
                  .filter((p) => p.status === "completed")
                  .reduce((s, p) => s + Number(p.amount || 0), 0)
                  .toLocaleString("en-IN")}
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[#E8E6F8] grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Name", value: order.user?.name },
            { label: "Email", value: order.user?.email },
            { label: "Phone", value: order.user?.phone },
            { label: "City", value: order.user?.city },
            { label: "State", value: order.user?.state },
          ].map((item) => (
            <div key={item.label}>
              <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wide">
                {item.label}
              </p>
              <p className="text-sm font-body text-[#1A1A2E] mt-0.5">
                {item.value || "–"}
              </p>
            </div>
          ))}
        </div>
      </div>

      <OrderDetailTabs order={order} onRefresh={load} />

      <ConfirmModal
        open={statusModal === "completed"}
        onOpenChange={(v) => !v && setStatusModal(null)}
        title="Mark Order Completed"
        description="This will mark the entire order as completed. The client will see it as done."
        confirmLabel="Mark Completed"
        confirmVariant="primary"
        onConfirm={() => handleStatusChange("completed")}
      />
      <ConfirmModal
        open={statusModal === "cancelled"}
        onOpenChange={(v) => !v && setStatusModal(null)}
        title="Cancel Order"
        description="This will cancel the order. This action can be reversed by setting status back to active."
        confirmLabel="Cancel Order"
        confirmVariant="danger"
        onConfirm={() => handleStatusChange("cancelled")}
      />
    </div>
  );
}

export function AdminUsersPage() {
  return <UsersTable />;
}

export function AdminStalledUsersPage() {
  return <StalledUsersPanel />;
}

export function AdminCustomerLogsPage() {
  return <CustomerLogsList />;
}

export function AdminCustomerLogDetailPage() {
  return <CustomerLogDetail />;
}

function UserDetailAvatar({ user, size = 48 }) {
  const [failed, setFailed] = useState(false);
  const src = resolveUserImageUrl(user?.profile_image);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={user?.name || "profile"}
        onError={() => setFailed(true)}
        className="rounded-full object-cover flex-shrink-0 border border-[#E8E6F8]"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#6C63FF] flex items-center justify-center text-white text-lg font-bold font-heading flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {user?.name?.[0]?.toUpperCase()}
    </div>
  );
}

export function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState("user");
  const [error, setError] = useState("");
  const [activeCas, setActiveCas] = useState([]);
  const [selectedCa, setSelectedCa] = useState("");
  const [caSaving, setCaSaving] = useState(false);
  const [caMsg, setCaMsg] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdToast, setPwdToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/users/${userId}`);
      setUser(data);
      setRole(data.role);
      setSelectedCa(data.assigned_ca?.id ? String(data.assigned_ca.id) : "");
    } catch {
      setError("Failed to load user");
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    API.get("/admin/active-cas")
      .then(({ data }) => setActiveCas(data || []))
      .catch(() => setActiveCas([]));
  }, []);

  const assignCa = async () => {
    if (!selectedCa) return;
    setCaSaving(true);
    setCaMsg("");
    try {
      const { data } = await API.put(`/admin/users/${userId}/assign-ca`, {
        ca_id: Number(selectedCa),
      });
      setCaMsg(`Assigned. ${data.realigned_orders} active order(s) realigned.`);
      await load();
    } catch (err) {
      setCaMsg(err.response?.data?.detail || "Failed to assign CA");
    }
    setCaSaving(false);
  };

  const updateRole = async () => {
    setSaving(true);
    try {
      await API.put(`/admin/users/${userId}/role`, { role });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update role");
    }
    setSaving(false);
  };

  const toggleStatus = async () => {
    setSaving(true);
    try {
      await API.put(`/admin/users/${userId}/status`, {
        is_active: user.is_active ? 0 : 1,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update status");
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage msg={error} />;
  if (!user) return null;

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate("/admin/users")}
        className="flex items-center gap-1.5 text-sm text-[#6C63FF] hover:underline font-body"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-1 space-y-4">
          <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
            <div className="flex items-center gap-3 mb-4">
              <UserDetailAvatar user={user} />
              <div>
                <p className="font-heading font-bold text-[#1A1A2E]">
                  {user.name}
                </p>
                <p className="text-xs text-[#9CA3AF] font-body">{user.email}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm font-body">
              {[
                ["Phone", user.phone],
                ["City", user.city],
                ["State", user.state],
                [
                  "Joined",
                  new Date(user.created_at).toLocaleDateString("en-IN"),
                ],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-[#9CA3AF]">{label}</span>
                  <span className="text-[#1A1A2E]">{val || "–"}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5 space-y-3">
            <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
              Role Management
            </p>
            <div className="flex gap-2">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="flex-1 text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onClick={updateRole}
                disabled={saving || role === user.role}
                className="px-3 py-2 text-xs font-body bg-[#6C63FF] text-white rounded-lg hover:bg-[#5B52CC] disabled:opacity-60"
              >
                {saving ? "…" : "Update"}
              </button>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#E8E6F8]">
              <span className="text-sm font-body text-[#1A1A2E]">
                Account Status
              </span>
              <button
                onClick={toggleStatus}
                disabled={saving}
                className={`text-xs font-body px-3 py-1.5 rounded-full transition-colors ${
                  user.is_active
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-red-100 text-red-600 hover:bg-red-200"
                }`}
              >
                {user.is_active
                  ? "Active — click to deactivate"
                  : "Inactive — click to activate"}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 font-body">{error}</p>}

            {isSuperAdmin && (
              <div className="pt-2 border-t border-[#E8E6F8]">
                <button
                  onClick={() => {
                    setPwdToast("");
                    setPwdOpen(true);
                  }}
                  className="w-full text-xs font-body font-medium px-3 py-2 rounded-lg bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/20 transition-colors"
                >
                  Change Password
                </button>
                {pwdToast && (
                  <p className="text-xs text-green-600 font-body mt-2">
                    {pwdToast}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5 space-y-3">
            <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
              Assigned CA
            </p>
            {user.assigned_ca ? (
              <div className="text-xs font-body text-[#6B7280]">
                <p className="text-[#1A1A2E] font-medium">
                  {user.assigned_ca.name}
                </p>
                <p>{user.assigned_ca.email}</p>
                {user.assigned_ca.is_active === 0 && (
                  <p className="text-red-500 mt-1">
                    CA is currently deactivated
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-[#9CA3AF] font-body">
                No CA assigned yet
              </p>
            )}
            <div className="flex gap-2">
              <select
                value={selectedCa}
                onChange={(e) => setSelectedCa(e.target.value)}
                className="flex-1 text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
              >
                <option value="">Select a CA…</option>
                {activeCas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.user_count} users)
                  </option>
                ))}
              </select>
              <button
                onClick={assignCa}
                disabled={
                  caSaving ||
                  !selectedCa ||
                  Number(selectedCa) === user.assigned_ca?.id
                }
                className="px-3 py-2 text-xs font-body bg-[#6C63FF] text-white rounded-lg hover:bg-[#5B52CC] disabled:opacity-60"
              >
                {caSaving ? "…" : "Assign"}
              </button>
            </div>
            {caMsg && (
              <p className="text-xs text-[#6B7280] font-body">{caMsg}</p>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E8E6F8]">
              <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">
                Orders ({user.orders?.length || 0})
              </h3>
            </div>
            {!user.orders || user.orders.length === 0 ? (
              <p className="text-center text-sm text-[#9CA3AF] font-body py-8">
                No orders yet
              </p>
            ) : (
              <div className="divide-y divide-[#E8E6F8]">
                {user.orders.map((o) => (
                  <Link
                    key={o.id}
                    to={`/admin/orders/${o.id}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-[#F5F4FB] transition-colors"
                  >
                    <div>
                      <p className="text-sm font-body font-medium text-[#1A1A2E]">
                        {o.service_name}
                      </p>
                      <p className="text-xs text-[#9CA3AF] font-body">
                        #{o.id} ·{" "}
                        {new Date(o.created_at).toLocaleDateString("en-IN")}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#E8E6F8] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#6C63FF] rounded-full"
                            style={{ width: `${o.progress_percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-[#6B7280]">
                          {o.progress_percentage}%
                        </span>
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={pwdOpen}
        target={user}
        endpoint="users"
        onClose={() => setPwdOpen(false)}
        onSuccess={() => setPwdToast("Password updated successfully.")}
      />
    </div>
  );
}

export function AdminCAProfilesPage() {
  return <CAProfilesPage />;
}

export function AdminTeamManagementPage() {
  return <AdminTeamPage />;
}

export function AdminMembersDirectoryPage() {
  return <MembersDirectory />;
}

export function AdminContactsPage() {
  return <ContactsPanel />;
}

export function AdminFunnelPage() {
  return <VisitorFunnelPanel />;
}

export function AdminAiReportPage() {
  return <AiReportPanel />;
}

export function AdminCallRequestsPage() {
  return <CallRequestsPage />;
}

export function AdminPaymentsPage() {
  return <PaymentsPanel />;
}

export function AdminLogsPage() {
  return <LogsPanel />;
}

export function AdminProfilePage() {
  return <ProfilePage />;
}

export function AdminInvoicesPage() {
  return <InvoicesPanel />;
}

export function AdminCreateCustomerPage() {
  return <CreateCustomerPage />;
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorMessage({ msg }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 font-body">
      {msg}
    </div>
  );
}

export default function AdminPage() {
  return <AdminLayout />;
}
